import type { RoadmapItem, RoadmapSource } from './roadmapData';

/**
 * A flattened roadmap tree-table row. The merged backlog nests four levels deep — a `source`
 * (the repo, e.g. "Frontend"/"Backend"), a `section` under it (e.g. "Datacenter"), an `area`
 * under that (e.g. "Domain Names"), and the `item` leaves — mirroring the per-repo `## Section →
 * Area` heading structure of the two TODO.md files. Source/section/area carry the expand chevron;
 * items are the leaves. `childCount` feeds the tree's `aria-setsize` (PatternFly renders the
 * toggle only when it is > 0); `itemTotal` is the leaf count shown in the row's "(N items)" label.
 */
export type RoadmapRow =
  | { kind: 'source'; key: string; label: string; level: 1; posInSet: number; childCount: number; itemTotal: number; expanded: boolean }
  | { kind: 'section'; key: string; label: string; level: 2; posInSet: number; childCount: number; itemTotal: number; expanded: boolean }
  | { kind: 'area'; key: string; label: string; level: 3; posInSet: number; childCount: number; itemTotal: number; expanded: boolean }
  | { kind: 'item'; key: string; level: 4; posInSet: number; item: RoadmapItem };

export const sourceKey = (source: string): string => `src:${source}`;
export const sectionKey = (source: string, section: string): string => `sec:${source}/${section}`;
export const areaKey = (source: string, section: string, area: string): string => `a:${source}/${section}/${area}`;

const countSectionItems = (areas: { items: unknown[] }[]): number => areas.reduce((sum, area) => sum + area.items.length, 0);

/** True when the query matches an item's id token, title, body, or status. A blank query matches all. */
export const itemMatchesQuery = (item: RoadmapItem, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (q === '') {
    return true;
  }
  return (
    `#${item.id}`.includes(q) ||
    item.title.toLowerCase().includes(q) ||
    item.description.toLowerCase().includes(q) ||
    item.status.toLowerCase().includes(q)
  );
};

/**
 * Keep only the parts matching `query`, dropping any area/section/source left empty. A group-name
 * match (source, section, or area) keeps that whole branch, so typing "backend" or "domain names"
 * surfaces everything under it; otherwise items are matched individually. A blank query returns the
 * input untouched (same reference) so there is no needless re-render churn.
 */
export const filterRoadmap = (sources: RoadmapSource[], query: string): RoadmapSource[] => {
  const q = query.trim().toLowerCase();
  if (q === '') {
    return sources;
  }
  return sources
    .map((src) => {
      const sourceMatches = src.source.toLowerCase().includes(q);
      return {
        ...src,
        sections: src.sections
          .map((section) => {
            const sectionMatches = sourceMatches || section.section.toLowerCase().includes(q);
            return {
              ...section,
              areas: section.areas
                .map((area) => {
                  const areaMatches = sectionMatches || area.area.toLowerCase().includes(q);
                  return { ...area, items: area.items.filter((item) => areaMatches || itemMatchesQuery(item, query)) };
                })
                .filter((area) => area.items.length > 0),
            };
          })
          .filter((section) => section.areas.length > 0),
      };
    })
    .filter((src) => src.sections.length > 0);
};

export interface FlattenOptions {
  /** Whether a given source/section/area key is currently expanded. */
  isExpanded: (key: string) => boolean;
  /** When true, every group is treated as expanded regardless of `isExpanded` (used while searching). */
  forceExpanded?: boolean;
}

/**
 * Flatten the nested roadmap into the ordered row list the tree table renders. A node's children
 * appear only while every ancestor up to it is expanded.
 */
export const flattenRoadmap = (sources: RoadmapSource[], options: FlattenOptions): RoadmapRow[] => {
  const { isExpanded, forceExpanded = false } = options;
  const rows: RoadmapRow[] = [];
  sources.forEach((src, srcIdx) => {
    const sKey = sourceKey(src.source);
    const sExpanded = forceExpanded || isExpanded(sKey);
    rows.push({
      kind: 'source',
      key: sKey,
      label: src.source,
      level: 1,
      posInSet: srcIdx + 1,
      childCount: src.sections.length,
      itemTotal: src.sections.reduce((sum, section) => sum + countSectionItems(section.areas), 0),
      expanded: sExpanded,
    });
    if (!sExpanded) {
      return;
    }
    src.sections.forEach((section, secIdx) => {
      const secKey = sectionKey(src.source, section.section);
      const secExpanded = forceExpanded || isExpanded(secKey);
      rows.push({
        kind: 'section',
        key: secKey,
        label: section.section,
        level: 2,
        posInSet: secIdx + 1,
        childCount: section.areas.length,
        itemTotal: countSectionItems(section.areas),
        expanded: secExpanded,
      });
      if (!secExpanded) {
        return;
      }
      section.areas.forEach((area, areaIdx) => {
        const aKey = areaKey(src.source, section.section, area.area);
        const aExpanded = forceExpanded || isExpanded(aKey);
        rows.push({
          kind: 'area',
          key: aKey,
          label: area.area,
          level: 3,
          posInSet: areaIdx + 1,
          childCount: area.items.length,
          itemTotal: area.items.length,
          expanded: aExpanded,
        });
        if (!aExpanded) {
          return;
        }
        area.items.forEach((item, itemIdx) => {
          rows.push({
            kind: 'item',
            key: `i:${src.source}/${section.section}/${area.area}/${item.id}`,
            level: 4,
            posInSet: itemIdx + 1,
            item,
          });
        });
      });
    });
  });
  return rows;
};

/** Every source, section and area key — used to seed the "all expanded by default" state. */
export const allGroupKeys = (sources: RoadmapSource[]): string[] => {
  const keys: string[] = [];
  sources.forEach((src) => {
    keys.push(sourceKey(src.source));
    src.sections.forEach((section) => {
      keys.push(sectionKey(src.source, section.section));
      section.areas.forEach((area) => keys.push(areaKey(src.source, section.section, area.area)));
    });
  });
  return keys;
};

/** Total number of leaf items across the whole merged backlog. */
export const countItems = (sources: RoadmapSource[]): number =>
  sources.reduce((total, src) => total + src.sections.reduce((sum, section) => sum + countSectionItems(section.areas), 0), 0);
